// components/PurchaseOrders.js
import React, { useState, useEffect } from 'react';
import '../../PurchaseOrders.css';
import purchaseOrderService from '../../../services/purchaseOrderService';
import supplierService from '../../../services/supplierService';
import productService from '../../../services/productService';
import pdfServiceAdvanced from '../../../services/pdfServiceAdvanced';

const PurchaseOrders = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredPurchaseOrders, setFilteredPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('order_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Loading states for individual operations
  const [createLoading, setCreateLoading] = useState(false);
  const [markOrderedLoading, setMarkOrderedLoading] = useState({});
  const [markDeliveredLoading, setMarkDeliveredLoading] = useState({});
  const [addToStockLoading, setAddToStockLoading] = useState({});
  const [addAllToStockLoading, setAddAllToStockLoading] = useState(false);
  const [updateDeliveryLoading, setUpdateDeliveryLoading] = useState(false);

  const [deliveryFormData, setDeliveryFormData] = useState({
    grn_number: '',
    received_date: new Date().toISOString().split('T')[0],
    notes: '',
    items: []
  });

  const [formData, setFormData] = useState({
    supplier_id: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery_date: '',
    notes: '',
    vat_type: 'percentage',
    vat_value: 15,
    discount: 0,
    items: [{ product_id: '', quantity: 1, unit_price: 0 }]
  });

  useEffect(() => {
    fetchPurchaseOrders();
    fetchSuppliers();
    fetchProducts();
  }, []);

  // Apply filters and search whenever relevant states change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [purchaseOrders, searchTerm, statusFilter, supplierFilter, dateFilter, sortBy, sortOrder]);

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const response = await purchaseOrderService.getAllPurchaseOrders();
      console.log('Purchase orders fetched:', response.data);
      setPurchaseOrders(response.data || []);
    } catch (err) {
      setError('Failed to fetch purchase orders');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierService.getActiveSuppliers();
      setSuppliers(response || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productService.getActiveProducts();
      setProducts(response || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const applyFiltersAndSearch = () => {
    let result = [...purchaseOrders];

    // Apply search filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(order =>
        order.po_number?.toLowerCase().includes(lowercasedSearch) ||
        order.supplier?.name?.toLowerCase().includes(lowercasedSearch) ||
        order.notes?.toLowerCase().includes(lowercasedSearch) ||
        order.grn_number?.toLowerCase().includes(lowercasedSearch) ||
        order.items?.some(item => 
          item.product?.name?.toLowerCase().includes(lowercasedSearch)
        )
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }

    // Apply supplier filter
    if (supplierFilter !== 'all') {
      result = result.filter(order => order.supplier_id == supplierFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0));
      const endOfDay = new Date(today.setHours(23, 59, 59, 999));

      switch (dateFilter) {
        case 'today':
          result = result.filter(order => {
            const orderDate = new Date(order.order_date);
            return orderDate >= startOfDay && orderDate <= endOfDay;
          });
          break;
        case 'this_week':
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          result = result.filter(order => {
            const orderDate = new Date(order.order_date);
            return orderDate >= startOfWeek && orderDate <= endOfDay;
          });
          break;
        case 'this_month':
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          result = result.filter(order => {
            const orderDate = new Date(order.order_date);
            return orderDate >= startOfMonth && orderDate <= endOfDay;
          });
          break;
        case 'last_30_days':
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          thirtyDaysAgo.setHours(0, 0, 0, 0);
          result = result.filter(order => {
            const orderDate = new Date(order.order_date);
            return orderDate >= thirtyDaysAgo && orderDate <= endOfDay;
          });
          break;
        default:
          break;
      }
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';

      // Handle nested properties
      if (sortBy === 'supplier.name') {
        aValue = a.supplier?.name || '';
        bValue = b.supplier?.name || '';
      }

      // Handle date comparison
      if (sortBy.includes('date')) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numeric comparison
      if (sortBy === 'total_amount') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredPurchaseOrders(result);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSupplierFilter = (e) => {
    setSupplierFilter(e.target.value);
  };

  const handleDateFilter = (e) => {
    setDateFilter(e.target.value);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSupplierFilter('all');
    setDateFilter('all');
    setSortBy('order_date');
    setSortOrder('desc');
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', quantity: 1, unit_price: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    if (field === 'product_id' && value) {
      const product = products.find(p => p.id == value);
      if (product) {
        newItems[index].unit_price = product.price;
      }
    }

    setFormData({ ...formData, items: newItems });
  };

  const handleVatTypeChange = (type) => {
    setFormData({
      ...formData,
      vat_type: type,
      vat_value: type === 'percentage' ? 15 : 0
    });
  };

  const handleVatValueChange = (value) => {
    setFormData({
      ...formData,
      vat_value: parseFloat(value) || 0
    });
  };

  const handleDiscountChange = (value) => {
    setFormData({
      ...formData,
      discount: parseFloat(value) || 0
    });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((total, item) => {
      return total + (item.quantity * (parseFloat(item.unit_price) || 0));
    }, 0);

    const discount = parseFloat(formData.discount) || 0;

    let vatAmount = 0;
    if (formData.vat_type === 'percentage') {
      vatAmount = (subtotal - discount) * (parseFloat(formData.vat_value) / 100);
    } else {
      vatAmount = parseFloat(formData.vat_value) || 0;
    }

    const total = subtotal - discount + vatAmount;

    return {
      subtotal: subtotal,
      discount: discount,
      vat: vatAmount,
      total: total
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      const totals = calculateTotals();

      const orderData = {
        ...formData,
        subtotal_amount: totals.subtotal,
        discount_amount: totals.discount,
        vat_amount: totals.vat,
        total_amount: totals.total,
        items: formData.items.map(item => ({
          ...item,
          unit_price: parseFloat(item.unit_price),
          quantity: parseInt(item.quantity)
        }))
      };

      await purchaseOrderService.createPurchaseOrder(orderData);
      setShowModal(false);
      setFormData({
        supplier_id: '',
        order_date: new Date().toISOString().split('T')[0],
        expected_delivery_date: '',
        notes: '',
        vat_type: 'percentage',
        vat_value: 15,
        discount: 0,
        items: [{ product_id: '', quantity: 1, unit_price: 0 }]
      });
      fetchPurchaseOrders();
    } catch (err) {
      setError('Failed to create purchase order');
      console.error('Error:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      console.log('Opening view modal for order:', orderId);
      const response = await purchaseOrderService.getPurchaseOrderById(orderId);
      console.log('Order details fetched:', response.data);
      setSelectedOrder(response.data);
      setShowViewModal(true);
    } catch (err) {
      setError('Failed to fetch purchase order details');
      console.error('Error:', err);
    }
  };

  const handleMarkOrdered = async (orderId) => {
    setMarkOrderedLoading(prev => ({ ...prev, [orderId]: true }));

    try {
      await purchaseOrderService.markAsOrdered(orderId);
      fetchPurchaseOrders();
    } catch (err) {
      setError('Failed to mark as ordered');
      console.error('Error:', err);
    } finally {
      setMarkOrderedLoading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleMarkDelivered = async (orderId) => {
    setMarkDeliveredLoading(prev => ({ ...prev, [orderId]: true }));

    try {
      const response = await purchaseOrderService.getPurchaseOrderById(orderId);
      const order = response.data;

      console.log('Opening delivery modal for order:', order);

      const deliveryItems = order.items.map(item => {
        const currentReceivedQty = item.received_quantity || 0;

        return {
          ...item,
          received_quantity: currentReceivedQty,
          previously_received: currentReceivedQty,
          new_received: 0,
          remaining_quantity: item.quantity - currentReceivedQty,
          add_to_stock: false,
          added_to_stock: false
        };
      });

      setDeliveryFormData({
        grn_number: `GRN-${order.po_number}-${Date.now().toString().slice(-4)}`,
        received_date: new Date().toISOString().split('T')[0],
        notes: '',
        items: deliveryItems
      });

      setSelectedOrder(order);
      setShowDeliveryModal(true);
    } catch (err) {
      setError('Failed to fetch purchase order details');
      console.error('Error:', err);
    } finally {
      setMarkDeliveredLoading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const handleDeliveryItemChange = (index, field, value) => {
    const newItems = [...deliveryFormData.items];

    if (field === 'new_received') {
      const newReceivedQty = parseInt(value) || 0;
      const previouslyReceived = newItems[index].previously_received || 0;

      const totalReceivedQty = previouslyReceived + newReceivedQty;
      const orderedQty = newItems[index].quantity;

      newItems[index].new_received = newReceivedQty;
      newItems[index].received_quantity = totalReceivedQty;
      newItems[index].remaining_quantity = orderedQty - totalReceivedQty;

      if (newReceivedQty > 0) {
        newItems[index].add_to_stock = true;
      }
    } else {
      newItems[index][field] = value;
    }

    setDeliveryFormData({
      ...deliveryFormData,
      items: newItems
    });
  };

  const handleAddItemToStock = async (item, orderId) => {
    setAddToStockLoading(prev => ({ ...prev, [item.id]: true }));

    try {
      // Use the new quantity update API instead of full product update
      await productService.updateProductQuantity(item.product_id, item.new_received);

      // Update the item to mark as added to stock
      const newItems = deliveryFormData.items.map(i =>
        i.id === item.id ? { ...i, added_to_stock: true } : i
      );

      setDeliveryFormData({
        ...deliveryFormData,
        items: newItems
      });

      alert(`Successfully added ${item.new_received} units of ${item.product?.name} to stock`);
    } catch (err) {
      setError('Failed to add item to stock');
      console.error('Error:', err);
    } finally {
      setAddToStockLoading(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const handleAddAllToStock = async () => {
    setAddAllToStockLoading(true);

    try {
      const itemsToAdd = deliveryFormData.items.filter(item =>
        item.add_to_stock && item.new_received > 0 && !item.added_to_stock
      );

      // Use Promise.all to handle multiple API calls efficiently
      await Promise.all(
        itemsToAdd.map(item =>
          productService.updateProductQuantity(item.product_id, item.new_received)
        )
      );

      // Mark all as added to stock
      const newItems = deliveryFormData.items.map(item => {
        if (itemsToAdd.some(i => i.id === item.id)) {
          return { ...item, added_to_stock: true };
        }
        return item;
      });

      setDeliveryFormData({
        ...deliveryFormData,
        items: newItems
      });

      alert(`Successfully added ${itemsToAdd.length} items to stock`);
    } catch (err) {
      setError('Failed to add items to stock');
      console.error('Error:', err);
    } finally {
      setAddAllToStockLoading(false);
    }
  };

  const handleDeliverySubmit = async (e) => {
    e.preventDefault();
    setUpdateDeliveryLoading(true);

    try {
      const deliveryData = {
        grn_number: deliveryFormData.grn_number,
        received_date: deliveryFormData.received_date,
        delivery_notes: deliveryFormData.notes,
        items: deliveryFormData.items.map(item => ({
          id: item.id,
          received_quantity: item.received_quantity
        }))
      };

      console.log('Sending delivery update:', deliveryData);

      await purchaseOrderService.updateDelivery(selectedOrder.id, deliveryData);

      setShowDeliveryModal(false);
      setDeliveryFormData({
        grn_number: '',
        received_date: new Date().toISOString().split('T')[0],
        notes: '',
        items: []
      });

      fetchPurchaseOrders();
      alert('Purchase order delivery updated successfully');
    } catch (err) {
      setError('Failed to update purchase order delivery');
      console.error('Error updating delivery:', err);
    } finally {
      setUpdateDeliveryLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (selectedOrder) {
      setPdfLoading(true);
      try {
        await pdfServiceAdvanced.generatePurchaseOrderPDF(selectedOrder);
      } catch (err) {
        setError('Failed to generate PDF');
        console.error('Error generating PDF:', err);
      } finally {
        setPdfLoading(false);
      }
    }
  };

  const handleDownloadRemainingItemsPDF = async () => {
    if (selectedOrder) {
      setPdfLoading(true);
      try {
        const remainingItems = deliveryFormData.items.filter(item =>
          item.remaining_quantity > 0
        );

        const remainingOrderData = {
          ...selectedOrder,
          items: remainingItems.map(item => ({
            ...item,
            quantity: item.remaining_quantity
          })),
          is_remaining_items: true
        };

        await pdfServiceAdvanced.generatePurchaseOrderPDF(remainingOrderData);
      } catch (err) {
        setError('Failed to generate remaining items PDF');
        console.error('Error generating PDF:', err);
      } finally {
        setPdfLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'status-draft',
      ordered: 'status-ordered',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled',
      partial: 'status-partial'
    };

    return `status-badge ${statusClasses[status] || 'status-draft'}`;
  };

  const totals = calculateTotals();

  const hasRemainingItems = deliveryFormData.items?.some(item => item.remaining_quantity > 0);
  const hasItemsToAdd = deliveryFormData.items?.some(item =>
    item.add_to_stock && item.new_received > 0 && !item.added_to_stock
  );

  // Loading spinner component
  const LoadingSpinner = ({ size = 'small' }) => (
    <div className={`loading-spinner ${size}`}>
      <div className="spinner"></div>
    </div>
  );

  console.log('Current selectedOrder:', selectedOrder);

  return (
    <div className="purchase-orders-container">
      <div className="purchase-orders-header">
        <div>
          <h1>Purchase Orders</h1>
          <p>Manage purchase orders from suppliers</p>
        </div>
        <div className="purchase-orders-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
            disabled={createLoading}
          >
            {createLoading ? <LoadingSpinner /> : 'Create Purchase Order'}
          </button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="purchase-orders-controls">
        <div className="purchase-orders-search-filter">
          <div className="purchase-orders-search-box">
            <input
              type="text"
              placeholder="Search PO number, supplier, notes, products..."
              value={searchTerm}
              onChange={handleSearch}
              className="purchase-orders-search-input"
            />
            <span className="purchase-orders-search-icon">🔍</span>
          </div>
          
          <div className="purchase-orders-filter-group">
            <select 
              value={statusFilter} 
              onChange={handleStatusFilter}
              className="purchase-orders-filter-select"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="ordered">Ordered</option>
              <option value="partial">Partial</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select 
              value={supplierFilter} 
              onChange={handleSupplierFilter}
              className="purchase-orders-filter-select"
            >
              <option value="all">All Suppliers</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>

            <select 
              value={dateFilter} 
              onChange={handleDateFilter}
              className="purchase-orders-filter-select"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="last_30_days">Last 30 Days</option>
            </select>

            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="purchase-orders-filter-select"
            >
              <option value="order_date">Sort by Date</option>
              <option value="po_number">Sort by PO Number</option>
              <option value="total_amount">Sort by Amount</option>
              <option value="status">Sort by Status</option>
            </select>

            <button 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="purchase-orders-sort-btn"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>

            {(searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || dateFilter !== 'all') && (
              <button 
                onClick={clearFilters}
                className="purchase-orders-clear-filters-btn"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="purchase-orders-results-info">
          Showing {filteredPurchaseOrders.length} of {purchaseOrders.length} purchase orders
          {searchTerm && (
            <span className="purchase-orders-search-term">
              for "{searchTerm}"
            </span>
          )}
        </div>
      </div>

      <div className="page-content">
        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div className="loading">
            <LoadingSpinner size="large" />
            Loading purchase orders...
          </div>
        ) : (
          <div className="purchase-orders-table-container">
            {filteredPurchaseOrders.length === 0 ? (
              <div className="purchase-orders-empty-state">
                <div className="purchase-orders-empty-icon">📋</div>
                <h3>No purchase orders found</h3>
                <p>
                  {searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || dateFilter !== 'all'
                    ? 'Try adjusting your search or filters to find what you\'re looking for.'
                    : 'No purchase orders available. Create your first purchase order to get started.'
                  }
                </p>
                {(searchTerm || statusFilter !== 'all' || supplierFilter !== 'all' || dateFilter !== 'all') && (
                  <button 
                    onClick={clearFilters}
                    className="btn btn-primary"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <table className="purchase-orders-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('po_number')} className="purchase-orders-sortable-header">
                      PO Number {getSortIcon('po_number')}
                    </th>
                    <th onClick={() => handleSort('supplier.name')} className="purchase-orders-sortable-header">
                      Supplier {getSortIcon('supplier.name')}
                    </th>
                    <th onClick={() => handleSort('order_date')} className="purchase-orders-sortable-header">
                      Order Date {getSortIcon('order_date')}
                    </th>
                    <th>Expected Delivery</th>
                    <th onClick={() => handleSort('total_amount')} className="purchase-orders-sortable-header">
                      Total Amount {getSortIcon('total_amount')}
                    </th>
                    <th onClick={() => handleSort('status')} className="purchase-orders-sortable-header">
                      Status {getSortIcon('status')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPurchaseOrders.map(order => (
                    <tr key={order.id}>
                      <td>{order.po_number}</td>
                      <td>{order.supplier?.name}</td>
                      <td>{new Date(order.order_date).toLocaleDateString()}</td>
                      <td>
                        {order.expected_delivery_date
                          ? new Date(order.expected_delivery_date).toLocaleDateString()
                          : 'N/A'
                        }
                      </td>
                      <td>Rs.{parseFloat(order.total_amount).toFixed(2)}</td>
                      <td>
                        <span className={getStatusBadge(order.status)}>
                          {order.status}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleViewOrder(order.id)}
                          >
                            View
                          </button>
                          {order.status === 'draft' && (
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleMarkOrdered(order.id)}
                              disabled={markOrderedLoading[order.id]}
                            >
                              {markOrderedLoading[order.id] ? (
                                <LoadingSpinner />
                              ) : (
                                'Mark Ordered'
                              )}
                            </button>
                          )}
                          {(order.status === 'ordered' || order.status === 'partial') && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleMarkDelivered(order.id)}
                              disabled={markDeliveredLoading[order.id]}
                            >
                              {markDeliveredLoading[order.id] ? (
                                <LoadingSpinner />
                              ) : order.status === 'partial' ? (
                                'Update Delivery'
                              ) : (
                                'Mark Delivered'
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Create Purchase Order Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal large-modal">
              <div className="modal-header">
                <h3>Create Purchase Order</h3>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Supplier *</label>
                      <select
                        value={formData.supplier_id}
                        onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                        required
                      >
                        <option value="">Select Supplier</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Order Date *</label>
                      <input
                        type="date"
                        value={formData.order_date}
                        onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Expected Delivery Date</label>
                      <input
                        type="date"
                        value={formData.expected_delivery_date}
                        onChange={(e) => setFormData({ ...formData, expected_delivery_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows="3"
                    />
                  </div>

                  {/* VAT Configuration Section */}
                  <div className="vat-configuration-section">
                    <h4>VAT & Discount Configuration</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>VAT Type</label>
                        <div className="radio-group">
                          <label className="radio-label">
                            <input
                              type="radio"
                              value="percentage"
                              checked={formData.vat_type === 'percentage'}
                              onChange={() => handleVatTypeChange('percentage')}
                            />
                            <span>Percentage (%)</span>
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              value="amount"
                              checked={formData.vat_type === 'amount'}
                              onChange={() => handleVatTypeChange('amount')}
                            />
                            <span>Fixed Amount (Rs.)</span>
                          </label>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          {formData.vat_type === 'percentage' ? 'VAT Percentage (%)' : 'VAT Amount (Rs.)'}
                        </label>
                        <input
                          type="number"
                          step={formData.vat_type === 'percentage' ? "0.1" : "0.01"}
                          min="0"
                          max={formData.vat_type === 'percentage' ? "100" : ""}
                          value={formData.vat_value}
                          onChange={(e) => handleVatValueChange(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Discount Amount (Rs.)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max={totals.subtotal}
                        value={formData.discount}
                        onChange={(e) => handleDiscountChange(e.target.value)}
                      />
                      <small className="help-text">
                        Maximum discount: Rs.{totals.subtotal.toFixed(2)}
                      </small>
                    </div>
                  </div>

                  <div className="items-section">
                    <div className="section-header">
                      <h4>Order Items</h4>
                      <button type="button" className="btn btn-sm btn-primary" onClick={handleAddItem}>
                        Add Item
                      </button>
                    </div>

                    {formData.items.map((item, index) => (
                      <div key={index} className="item-row">
                        <div className="item-form-row">
                          <div className="form-group">
                            <label>Product</label>
                            <select
                              value={item.product_id}
                              onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                              required
                            >
                              <option value="">Select Product</option>
                              {products.map(product => (
                                <option key={product.id} value={product.id}>
                                  {product.name} - Rs.{product.price}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Quantity</label>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
                              min="1"
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Unit Price</label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label>Total</label>
                            <input
                              type="text"
                              value={`Rs.${(item.quantity * item.unit_price).toFixed(2)}`}
                              readOnly
                              className="readonly"
                            />
                          </div>
                          {formData.items.length > 1 && (
                            <div className="form-group">
                              <button
                                type="button"
                                className="btn btn-sm btn-danger remove-btn"
                                onClick={() => handleRemoveItem(index)}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Amount Summary */}
                    <div className="amount-summary">
                      <h4>Amount Summary</h4>
                      <div className="summary-row">
                        <span>Subtotal:</span>
                        <span>Rs.{totals.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="summary-row">
                        <span>Discount:</span>
                        <span>- Rs.{totals.discount.toFixed(2)}</span>
                      </div>
                      <div className="summary-row">
                        <span>
                          VAT {formData.vat_type === 'percentage' ? `(${formData.vat_value}%)` : '(Fixed Amount)'}:
                        </span>
                        <span>+ Rs.{totals.vat.toFixed(2)}</span>
                      </div>
                      <div className="summary-row total">
                        <span>Grand Total:</span>
                        <span>Rs.{totals.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={createLoading}>
                    {createLoading ? (
                      <>
                        <LoadingSpinner />
                        Creating...
                      </>
                    ) : (
                      'Create Purchase Order'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Purchase Order Modal */}
        {showViewModal && selectedOrder && (
          <div className="modal-overlay">
            <div className="modal xlarge-modal">
              <div className="modal-header">
                <h3>Purchase Order Details</h3>
                <button className="close-btn" onClick={() => setShowViewModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="po-details">
                  <div className="po-header">
                    <h2>Purchase Order: {selectedOrder.po_number || 'N/A'}</h2>
                    <div className="po-meta">
                      <div className="meta-item">
                        <span className="meta-label">Supplier</span>
                        <span className="meta-value">{selectedOrder.supplier?.name || 'N/A'}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Order Date</span>
                        <span className="meta-value">
                          {selectedOrder.order_date ? new Date(selectedOrder.order_date).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Expected Delivery</span>
                        <span className="meta-value">
                          {selectedOrder.expected_delivery_date
                            ? new Date(selectedOrder.expected_delivery_date).toLocaleDateString()
                            : 'N/A'
                          }
                        </span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">Status</span>
                        <span className={`meta-value ${getStatusBadge(selectedOrder.status)}`}>
                          {selectedOrder.status || 'draft'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="po-body">
                    {selectedOrder.notes && (
                      <div className="form-group">
                        <label>Notes</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                          {selectedOrder.notes}
                        </div>
                      </div>
                    )}

                    {/* VAT and Discount Summary */}
                    <div className="amount-summary-preview">
                      <h4>Amount Summary</h4>
                      <div className="summary-row">
                        <span>Subtotal:</span>
                        <span>Rs.{parseFloat(selectedOrder.subtotal_amount || selectedOrder.total_amount || 0).toFixed(2)}</span>
                      </div>

                      {(selectedOrder.discount_amount > 0) && (
                        <div className="summary-row">
                          <span>Discount:</span>
                          <span>- Rs.{parseFloat(selectedOrder.discount_amount || 0).toFixed(2)}</span>
                        </div>
                      )}

                      {(selectedOrder.vat_amount > 0) && (
                        <div className="summary-row">
                          <span>
                            VAT {selectedOrder.vat_type === 'percentage'
                              ? `(${selectedOrder.vat_value}%)`
                              : '(Fixed Amount)'}:
                          </span>
                          <span>+ Rs.{parseFloat(selectedOrder.vat_amount || 0).toFixed(2)}</span>
                        </div>
                      )}

                      <div className="summary-row total">
                        <span>Grand Total:</span>
                        <span>Rs.{parseFloat(selectedOrder.total_amount || 0).toFixed(2)}</span>
                      </div>

                      {/* VAT Details Section */}
                      <div className="vat-details-section">
                        <h5>VAT Configuration</h5>
                        <div className="vat-details-grid">
                          <div className="vat-detail-item">
                            <span className="vat-label">VAT Type:</span>
                            <span className="vat-value">
                              {selectedOrder.vat_type === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                            </span>
                          </div>
                          <div className="vat-detail-item">
                            <span className="vat-label">VAT Value:</span>
                            <span className="vat-value">
                              {selectedOrder.vat_type === 'percentage'
                                ? `${selectedOrder.vat_value}%`
                                : `Rs.${parseFloat(selectedOrder.vat_value || 0).toFixed(2)}`
                              }
                            </span>
                          </div>
                          <div className="vat-detail-item">
                            <span className="vat-label">VAT Amount:</span>
                            <span className="vat-value">
                              Rs.{parseFloat(selectedOrder.vat_amount || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="vat-detail-item">
                            <span className="vat-label">Discount Amount:</span>
                            <span className="vat-value">
                              Rs.{parseFloat(selectedOrder.discount_amount || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <h4>Order Items</h4>
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      <table className="items-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Quantity</th>
                            <th>Unit Price</th>
                            <th>Total Amount</th>
                            <th>Received</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedOrder.items.map((item, index) => (
                            <tr key={index}>
                              <td>{item.product?.name || 'N/A'}</td>
                              <td>{item.quantity || 0}</td>
                              <td className="amount-cell">Rs.{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                              <td className="amount-cell">Rs.{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</td>
                              <td>{item.received_quantity || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="no-items">No items found for this purchase order.</div>
                    )}
                  </div>

                  <div className="po-footer">
                    <button
                      className="btn btn-success download-btn"
                      onClick={handleDownloadPDF}
                      disabled={pdfLoading}
                    >
                      {pdfLoading ? (
                        <>
                          <LoadingSpinner />
                          Generating PDF...
                        </>
                      ) : (
                        'Download PDF'
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delivery Confirmation Modal */}
        {showDeliveryModal && selectedOrder && (
          <div className="modal-overlay">
            <div className="modal xlarge-modal">
              <div className="modal-header">
                <h3>Receive Delivery - {selectedOrder.po_number}</h3>
                <button className="close-btn" onClick={() => setShowDeliveryModal(false)}>×</button>
              </div>
              <form onSubmit={handleDeliverySubmit}>
                <div className="modal-body">
                  <div className="delivery-header">
                    <div className="delivery-meta">
                      <div className="meta-grid">
                        <div className="meta-item">
                          <span className="meta-label">Supplier</span>
                          <span className="meta-value">{selectedOrder.supplier?.name || 'N/A'}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Order Date</span>
                          <span className="meta-value">
                            {selectedOrder.order_date ? new Date(selectedOrder.order_date).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">GRN Number</span>
                          <input
                            type="text"
                            className="form-control"
                            value={deliveryFormData.grn_number}
                            onChange={(e) => setDeliveryFormData({
                              ...deliveryFormData,
                              grn_number: e.target.value
                            })}
                            required
                          />
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Received Date</span>
                          <input
                            type="date"
                            className="form-control"
                            value={deliveryFormData.received_date}
                            onChange={(e) => setDeliveryFormData({
                              ...deliveryFormData,
                              received_date: e.target.value
                            })}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Delivery Notes</label>
                    <textarea
                      className="form-control"
                      value={deliveryFormData.notes}
                      onChange={(e) => setDeliveryFormData({
                        ...deliveryFormData,
                        notes: e.target.value
                      })}
                      rows="3"
                      placeholder="Any notes about this delivery..."
                    />
                  </div>

                  <div className="delivery-items-section">
                    <h4>Received Items</h4>
                    {deliveryFormData.items && deliveryFormData.items.length > 0 ? (
                      <div className="items-table-container">
                        <table className="delivery-items-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Ordered Qty</th>
                              <th>Previously Received</th>
                              <th>New Received</th>
                              <th>Total Received</th>
                              <th>Remaining Qty</th>
                              <th>Unit Price</th>
                              <th>Add to Stock</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deliveryFormData.items.map((item, index) => (
                              <tr key={item.id || index} className={item.remaining_quantity > 0 ? 'has-remaining' : ''}>
                                <td>{item.product?.name || 'N/A'}</td>
                                <td>{item.quantity || 0}</td>
                                <td>{item.previously_received || 0}</td>
                                <td>
                                  <input
                                    type="number"
                                    className="form-control qty-input"
                                    min="0"
                                    max={item.remaining_quantity + (item.new_received || 0)}
                                    value={item.new_received || 0}
                                    onChange={(e) => handleDeliveryItemChange(
                                      index,
                                      'new_received',
                                      parseInt(e.target.value) || 0
                                    )}
                                  />
                                </td>
                                <td>{item.received_quantity || 0}</td>
                                <td className={item.remaining_quantity > 0 ? 'remaining-warning' : ''}>
                                  {item.remaining_quantity || 0}
                                </td>
                                <td>Rs.{parseFloat(item.unit_price || 0).toFixed(2)}</td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={item.add_to_stock || false}
                                    onChange={(e) => handleDeliveryItemChange(
                                      index,
                                      'add_to_stock',
                                      e.target.checked
                                    )}
                                    disabled={(item.new_received || 0) === 0 || item.added_to_stock}
                                  />
                                  {item.added_to_stock && (
                                    <span className="badge-success">Added</span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => handleAddItemToStock(item, selectedOrder.id)}
                                    disabled={!item.add_to_stock || (item.new_received || 0) === 0 || item.added_to_stock || addToStockLoading[item.id]}
                                  >
                                    {addToStockLoading[item.id] ? (
                                      <LoadingSpinner />
                                    ) : (
                                      'Add to Stock'
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="no-items">No items found for this purchase order.</div>
                    )}
                  </div>

                  <div className="delivery-summary">
                    <div className="summary-actions">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={handleAddAllToStock}
                        disabled={!hasItemsToAdd || addAllToStockLoading}
                      >
                        {addAllToStockLoading ? (
                          <>
                            <LoadingSpinner />
                            Adding All to Stock...
                          </>
                        ) : (
                          'Add All to Stock'
                        )}
                      </button>

                      {hasRemainingItems && (
                        <button
                          type="button"
                          className="btn btn-warning"
                          onClick={handleDownloadRemainingItemsPDF}
                          disabled={pdfLoading}
                        >
                          {pdfLoading ? (
                            <>
                              <LoadingSpinner />
                              Generating...
                            </>
                          ) : (
                            'Download Remaining Items PDF'
                          )}
                        </button>
                      )}
                    </div>

                    <div className="status-preview">
                      <h5>Status Preview:</h5>
                      <div className="status-indicators">
                        {deliveryFormData.items?.every(item => (item.received_quantity || 0) >= (item.quantity || 0)) && (
                          <span className="status-indicator full">✓ All items fully received</span>
                        )}
                        {deliveryFormData.items?.some(item => (item.received_quantity || 0) > 0 && (item.received_quantity || 0) < (item.quantity || 0)) && (
                          <span className="status-indicator partial">⚠ Partial delivery</span>
                        )}
                        {deliveryFormData.items?.every(item => (item.received_quantity || 0) === 0) && (
                          <span className="status-indicator none">No items received</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowDeliveryModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updateDeliveryLoading}>
                    {updateDeliveryLoading ? (
                      <>
                        <LoadingSpinner />
                        Updating...
                      </>
                    ) : (
                      'Update Delivery'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrders;